// src/components/Profile/ProfileForm.js
import React, { useState } from 'react'
import {
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress
} from '@mui/material'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '../../firebase'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

/**
 * onProfileSave: Profil başarıyla güncellenince çağrılacak callback (Profile.js'ten geliyor).
 */
export default function ProfileForm({ userData, setError, setSuccess, onProfileSave }) {
  const { t } = useTranslation()  // i18n Hook

  const [firstName, setFirstName] = useState(userData.firstName || '')
  const [lastName, setLastName] = useState(userData.lastName || '')
  const [phone, setPhone] = useState(userData.phone || '')
  const [organizationName, setOrganizationName] = useState(userData.organizationName || '')
  const [billingInfo, setBillingInfo] = useState(userData.billingInfo || '')
  const [updating, setUpdating] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setUpdating(true)
    setError('')
    setSuccess('')

    try {
      const userDocRef = doc(db, 'users', userData.uid)
      const updateData = {
        firstName,
        lastName,
        organizationName: organizationName.trim(),
        billingInfo: billingInfo.trim(),
        phone: phone.trim() === '' ? null : phone
      }
      // Firestore'da profil bilgisini güncelle
      await updateDoc(userDocRef, updateData)

      setSuccess(t('profileForm.updateSuccess')) // "Profiliniz başarıyla güncellendi!"

      // Form güncellemesi başarılı -> Profile.js'ten gelen callback çalıştır
      if (onProfileSave) {
        onProfileSave()
      }
    } catch (err) {
      console.error('Profil güncellenirken hata:', err)
      setError(t('profileForm.updateError')) // "Profil güncellenirken bir hata oluştu."
    } finally {
      setUpdating(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
        {t('profileForm.title')}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {/* E-posta (readonly) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('profileForm.emailLabel')}
            variant="outlined"
            value={userData.email}
            disabled
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(248,250,252,0.9)'
              }
            }}
          />
        </Grid>

        {/* İsim / Soyisim */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label={t('profileForm.firstNameLabel')}
            variant="outlined"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(248,250,252,0.9)'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label={t('profileForm.lastNameLabel')}
            variant="outlined"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(248,250,252,0.9)'
              }
            }}
          />
        </Grid>

        {/* Telefon Numarası */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('profileForm.phoneLabel')}
            variant="outlined"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('profileForm.phonePlaceholder')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(248,250,252,0.9)'
              }
            }}
          />
        </Grid>

        {/* Organizasyon */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('profileForm.organizationLabel')}
            variant="outlined"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder={t('profileForm.organizationPlaceholder')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(248,250,252,0.9)'
              }
            }}
          />
        </Grid>

        {/* Fatura Bilgileri */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('profileForm.billingInfoLabel')}
            variant="outlined"
            value={billingInfo}
            onChange={(e) => setBillingInfo(e.target.value)}
            placeholder={t('profileForm.billingInfoPlaceholder')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(248,250,252,0.9)'
              }
            }}
          />
        </Grid>
      </Grid>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        sx={{
          mt: 3,
          py: 1.3,
          borderRadius: 999,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          boxShadow: '0 14px 30px rgba(37,99,235,0.4)'
        }}
        disabled={updating}
      >
        {updating ? <CircularProgress size={24} color="inherit" /> : t('profileForm.submitButton')}
      </Button>
    </form>
  )
}
